import { Box, Typography, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import React from "react";

// Inner class component — handles the actual error catching logic.
// Hooks cannot be used in class components, so translation is injected via props.
class ErrorBoundaryClass extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        if (process.env.NODE_ENV !== "production") {
            console.error("ErrorBoundary caught:", error, info);
        }
    }

    handleReload = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            const { t } = this.props;
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="100vh"
                    gap={2}
                    p={4}
                >
                    <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
                    <Typography variant="h5" fontWeight={600}>
                        {t("ErrorHeader", "Something went wrong")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
                        {t("ErrorMessage", "An unexpected error occurred. Please try reloading the page.")}
                    </Typography>
                    <Button variant="contained" onClick={this.handleReload}>
                        {t("ReloadPage", "Reload page")}
                    </Button>
                </Box>
            );
        }

        return this.props.children;
    }
}

// Outer functional wrapper — uses the hook and passes `t` down as a prop.
function ErrorBoundary({ children }) {
    const { t } = useTranslation();
    return <ErrorBoundaryClass t={t}>{children}</ErrorBoundaryClass>;
}

export default ErrorBoundary;
